import { Group, Stack } from "@mantine/core"
import { AreYouSureModal, SectionHeader } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { SubscriptionPlan } from "@tfg/types/endpoints/subscriptionPlan"

import { queryClient } from "~/backend/queryClient"
import { useSaveSubscriptionPlan } from "~/gen/api"
import { SubscriptionPlanCard } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/components/SubscriptionPlanCard"
import { useOrganizationSettings } from "~/pages/Brand/CommunicationStudio/contexts/organizationSettings/useOrganizationSettings"
import { useOrganizationSubscriptionPlanPermissions } from "~/pages/Brand/CommunicationStudio/contexts/organizationSubscriptionPlanPermissions/useOrganizationSubscriptionPlanPermissions"
import { useAuth } from "~/store/auth-context"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const SubscriptionPlans = () => {
  const { t } = useTranslation("communicationStudio")
  const { isAdmin } = useAuth()
  const organizationSettings = useOrganizationSettings()
  const organizationId = organizationSettings.organization.id
  const organizationSlug = organizationSettings.organization.slug

  const { setSubscriptionPlan: setSubscriptionPlanForPermissions } =
    useOrganizationSubscriptionPlanPermissions()

  const currentSubscriptionPlan =
    organizationSettings.organization.settings.subscriptionPlan

  const [pendingSubscriptionPlan, setPendingSubscriptionPlan] =
    useState<SubscriptionPlan | null>(null)
  const [isModalOpened, setIsModalOpened] = useState(false)

  const { mutate: saveSubscriptionPlan } = useSaveSubscriptionPlan({
    mutation: {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["allOrganizationSettings", organizationId],
        })
        setSubscriptionPlanForPermissions(
          variables.data.subscriptionPlan as SubscriptionPlan
        )
        showSuccessNotification({
          message: t("tabs.general.subscriptionPlanChangeSuccess.message"),
          title: t("tabs.general.subscriptionPlanChangeSuccess.title"),
        })
      },
    },
  })

  const handleSubscriptionPlanChange = (subscriptionPlan: SubscriptionPlan) => {
    if (subscriptionPlan === currentSubscriptionPlan || !isAdmin) {
      return
    }
    setPendingSubscriptionPlan(subscriptionPlan)
    setIsModalOpened(true)
  }

  const handleConfirmSubscriptionPlanChange = () => {
    if (pendingSubscriptionPlan) {
      saveSubscriptionPlan({
        organizationId,
        data: {
          subscriptionPlan: pendingSubscriptionPlan,
          slug: organizationSlug,
        },
      })
    }
    setPendingSubscriptionPlan(null)
  }

  const handleCancelSubscriptionPlanChange = () => {
    setPendingSubscriptionPlan(null)
  }

  return (
    <>
      <Stack w="fit-content">
        <SectionHeader
          title={t("tabs.general.subscriptionPlans.title")}
          subtitle={t("tabs.general.subscriptionPlans.subtitle")}
          w="100%"
          maw="90rem"
        />
        <Group w="100%" justify="flex-start" gap="md" h="fit-content">
          {Object.values(SubscriptionPlan)
            .filter(
              (subscriptionPlan) => subscriptionPlan !== SubscriptionPlan.NONE
            )
            .map((subscriptionPlan) => (
              <SubscriptionPlanCard
                key={subscriptionPlan}
                subscriptionPlan={subscriptionPlan}
                name={t(
                  `tabs.general.subscriptionPlans.${subscriptionPlan}.name`
                )}
                isAdmin={isAdmin}
                description={t(
                  `tabs.general.subscriptionPlans.${subscriptionPlan}.description`
                )}
                checked={subscriptionPlan === currentSubscriptionPlan}
                onClick={handleSubscriptionPlanChange}
              />
            ))}
        </Group>
      </Stack>
      <AreYouSureModal
        opened={isModalOpened}
        onClose={() => {
          setIsModalOpened(false)
          setPendingSubscriptionPlan(null)
        }}
        onConfirm={handleConfirmSubscriptionPlanChange}
        onCancel={handleCancelSubscriptionPlanChange}
        areYouSureText={t("tabs.general.subscriptionPlanModal.areYouSureText", {
          name: pendingSubscriptionPlan
            ? t(
                `tabs.general.subscriptionPlans.${pendingSubscriptionPlan}.name`
              )
            : "",
        })}
        confirmText={t("tabs.general.subscriptionPlanModal.confirmText")}
        cancelText={t("tabs.general.subscriptionPlanModal.cancelText")}
        title={t("tabs.general.subscriptionPlanModal.title")}
      />
    </>
  )
}

// t("tabs.general.subscriptionPlans.NONE.name")
// t("tabs.general.subscriptionPlans.STARTER.description")
// t("tabs.general.subscriptionPlans.STARTER.name")
// t("tabs.general.subscriptionPlans.ADVANCED.name")
// t("tabs.general.subscriptionPlans.ADVANCED.description")
// t("tabs.general.subscriptionPlans.EXPERT.name")
// t("tabs.general.subscriptionPlans.EXPERT.description")
